import { pipe } from 'fp-ts/function';
import * as Either from 'fp-ts/Either';
import * as t from 'io-ts';
import { formatValidationErrors } from 'io-ts-reporters';
import isNil from 'lodash-es/isNil';
import { decoding } from './ErrorAsync';
/**
 * Formats errors with [io-ts-reporters](https://github.com/gillchristian/io-ts-reporters).
 *
 * @example
 * import * as t from 'io-ts'
 * import { left } from 'fp-ts/Either'
 * import { Decoding } from '@catawiki/runtime-toolkit'
 *
 * assert.deepStrictEqual(
 *   Decoding.humanizeErrors(t.number.decode('123')),
 *   left(['Expecting number but instead got: "123"'])
 * )
 *
 */
export const humanizeErrors = Either.mapLeft(formatValidationErrors);
/**
 * Decodes a value with an io-ts decoder.
 *
 * In case of error (Left) it humanizes errors and transforms to `ErrorAsync.Decoding`.
 *
 *  @example
 * import * as t from 'io-ts'
 * import { right, left } from 'fp-ts/Either'
 * import { Decoding, ErrorAsync } from '@catawiki/runtime-toolkit'
 *
 * assert.deepStrictEqual(
 *   Decoding.decodeWith(t.string)('foo'),
 *   right('foo')
 * )
 * assert.deepStrictEqual(
 *   Decoding.decodeWith(t.string)(123),
 *   left(ErrorAsync.decoding(value)(['Expecting string but instead got: 123']))
 * )
 *
 * @deprecated in favour of decode
 */
export const decodeWith = (decoder) => (response) => pipe(response, decoder.decode, humanizeErrors, Either.mapLeft(decoding(response)));
export class DecodingError extends Error {
    errors;
    payload;
    constructor(errors, payload) {
        super('Decoding error');
        this.name = 'DecodingError';
        this.errors = errors;
        this.payload = payload;
        Object.setPrototypeOf(this, DecodingError.prototype);
    }
}
export const isDecodingError = (error) => error instanceof DecodingError;
/**
 * Decodes a value with an io-ts decoder.
 *
 * In case of error it humanizes errors and then throws a DecodingError.
 *
 *  @example
 * import * as t from 'io-ts'
 * import { Decoding } from '@catawiki/runtime-toolkit'
 *
 * assert.deepStrictEqual(
 *   Decoding.decode(t.string)('foo'),
 *   'foo'
 * )
 * assert.throws(
 *   () => Decoding.decode(t.string)(123),
 *   new DecodingError(['Expecting string but instead got: 123'], 123)
 * )
 */
export const decode = (decoder) => (response) => pipe(response, decoder.decode, humanizeErrors, Either.getOrElse((errors) => {
    throw new DecodingError(errors, response);
}));
/**
 * io-ts codec for nullable types (`A | null | undefined`).
 *
 * @example
 * import * as t from 'io-ts'
 * import { right } from 'fp-ts/Either'
 * import { Decoding } from '@catawiki/runtime-toolkit'
 *
 * const user = t.interface({ name: Decoding.nullable(t.string) })
 *
 * assert.deepStrictEqual(user.decode({ name: 'Jane Doe' }), right({ name: 'Jane Doe' }))
 * assert.deepStrictEqual(user.decode({ name: null }), right({ name: null }))
 * assert.deepStrictEqual(user.decode({}), right({ name: undefined }))
 * assert.deepStrictEqual(user.decode({ name: undefined }), right({ name: undefined }))
 *
 */
export const nullable = (codec, name) => t.union([t.null, t.undefined, codec], name);
/**
 * Parses a JSON string to an object.
 * In case of error it returns the default value.
 *
 * @example
 * import { Decoding } from '@catawiki/runtime-toolkit'
 *
 * const parseJson = Decoding.parseJson({})
 *
 * assert.deepStrictEqual(parseJson('{"foo": "bar"}'), { foo: 'bar' })
 * assert.deepStrictEqual(parseJson('invalid json'), {})
 * assert.deepStrictEqual(parseJson(null), {})
 */
export const parseJson = (def) => (str) => {
    if (isNil(str)) {
        return def;
    }
    try {
        return JSON.parse(str);
    }
    catch {
        return def;
    }
};
